export type AudioCapture = {
  blob: Blob
  mimeType: string
  durationMs: number
}

export function createAudioRecorder(): {
  start: (deviceId?: string) => Promise<void>
  stop: () => Promise<AudioCapture | null>
  isRecording: () => boolean
} {
  let media: MediaRecorder | null = null
  let chunks: BlobPart[] = []
  let startedAt = 0
  let stream: MediaStream | null = null

  const pickMime = () => {
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
    return candidates.find((m) => MediaRecorder.isTypeSupported(m)) || ''
  }

  return {
    isRecording: () => media?.state === 'recording',
    start: async (deviceId?: string) => {
      const audio: MediaTrackConstraints | boolean = deviceId
        ? { deviceId: { exact: deviceId } }
        : true
      stream = await navigator.mediaDevices.getUserMedia({ audio })
      const mimeType = pickMime()
      media = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      chunks = []
      media.ondataavailable = (ev) => {
        if (ev.data.size > 0) chunks.push(ev.data)
      }
      startedAt = Date.now()
      media.start(200)
    },
    stop: async () => {
      if (!media) return null
      const rec = media
      const result = await new Promise<AudioCapture | null>((resolve) => {
        rec.onstop = () => {
          const mimeType = rec.mimeType || 'audio/webm'
          const blob = new Blob(chunks, { type: mimeType })
          stream?.getTracks().forEach((t) => t.stop())
          stream = null
          media = null
          if (blob.size === 0) {
            resolve(null)
            return
          }
          resolve({ blob, mimeType, durationMs: Date.now() - startedAt })
        }
        rec.stop()
      })
      return result
    },
  }
}

export async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer()
  const bytes = new Uint8Array(buf)
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}
